import { useEffect, useMemo, useState } from "react";
import { Modal, Input } from "../../../components/ui";
import { COLORS } from "../../../theme/colors";
import { widgetApi } from "../../../api/client";
import WidgetAllowedDomains from "./WidgetAllowedDomains";

/**
 * One-click embed-snippet generator for an agent.
 *
 * Why the flow looks the way it does: the platform's widget API keys are
 * hashed at rest. The raw `ak_live_…` value is shown ONCE at creation —
 * if the user closes the modal and reopens it, the same key cannot be
 * surfaced again. So this modal creates a fresh widget-scoped key per
 * embed, names it after the agent + timestamp, and writes its raw value
 * into the generated snippet right at that moment. The user copies the
 * snippet, the key stays revocable via Admin → API Keys, and there is no
 * "ghost" of an old key the user has to remember or rotate.
 *
 * The customisation fields (title / welcome / colour) are saved in the
 * snippet itself, not on the key, so the user can change them later by
 * editing the script tag without rotating credentials.
 */
// Pure helper (named export for unit testing) — builds the <script> embed
// snippet from the current customisation + generated key. Extracted from the
// component's useMemo so it can be exercised directly; wired back below.
export function buildEmbedSnippet({ serverUrl, apiKey, agentId, title, welcome, color }) {
  if (!agentId) return "";
  const safe = (s) => String(s ?? "").replace(/"/g, '\\"');
  return [
    "<script",
    `  src="${serverUrl}/static/widget/agentic-widget.js"`,
    `  data-agentic-api-key="${apiKey || "ak_live_…paste-key-here…"}"`,
    `  data-agentic-agent="${agentId}"`,
    `  data-agentic-title="${safe(title)}"`,
    `  data-agentic-welcome="${safe(welcome)}"`,
    `  data-agentic-color="${safe(color)}">`,
    "</script>",
  ].join("\n");
}

export default function EmbedSnippetModal({ open, onClose, agent }) {
  const agentName = agent?.name || "Assistant";
  const [title, setTitle] = useState(agentName);
  const [welcome, setWelcome] = useState(
    `Hi! I'm ${agentName}. How can I help?`
  );
  const [color, setColor] = useState("#4F46E5");
  const [generating, setGenerating] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [keyId, setKeyId] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Reset state every time the modal opens, so re-opening for the same
  // agent always starts on the customisation step (never resurfaces an
  // old key — see top-of-file rationale).
  useEffect(() => {
    if (open) {
      setTitle(agentName);
      setWelcome(`Hi! I'm ${agentName}. How can I help?`);
      setColor("#4F46E5");
      setApiKey(null);
      setKeyId(null);
      setError(null);
      setCopied(false);
    }
  }, [open, agentName]);

  const serverUrl = useMemo(
    () => (typeof window !== "undefined" ? window.location.origin : ""),
    []
  );

  // The platform serves the widget bundle off its API host. window.location
  // here is the platform UI itself, so origin is the right answer for every
  // environment (local kind, staging, prod). Customer embed sites use a
  // different origin, which is fine — the widget JS bakes in a server URL
  // when loaded.
  const snippet = useMemo(
    () => buildEmbedSnippet({ serverUrl, apiKey, agentId: agent?.id, title, welcome, color }),
    [agent?.id, apiKey, serverUrl, title, welcome, color]
  );

  async function handleGenerate() {
    setGenerating(true);
    setError(null);
    try {
      // Name the key after the agent so the admin can find + revoke it
      // later. Timestamp suffix keeps it unique across repeated embeds.
      const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");
      const name = `Embed: ${agentName} (${stamp})`;
      const res = await widgetApi.createKey(name, ["widget"]);
      const data = res?.data || res;
      if (!data?.key) throw new Error("Server response did not contain a key");
      setApiKey(data.key);
      setKeyId(data.id);
    } catch (e) {
      setError(e?.message || "Failed to create widget key");
    } finally {
      setGenerating(false);
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setError("Could not copy to clipboard — select the snippet manually.");
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Embed this agent on a website"
      width={600}
      footer={
        <>
          <button
            onClick={onClose}
            style={{
              padding: "7px 14px",
              borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
              background: "transparent",
              color: COLORS.text,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ fontSize: 12, color: COLORS.textMuted, margin: 0, lineHeight: 1.5 }}>
          Paste the snippet into any HTML page — a marketing site, support
          portal, intranet — and your visitors get a floating chat bubble
          wired to <strong style={{ color: COLORS.text }}>{agentName}</strong>.
          The widget script lives on this platform, so no build step or
          npm install on the customer's side.
        </p>

        {/* ── Customisation step (always visible — tweak before generating) */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Header title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={agentName} />
          </Field>
          <Field label="Accent color">
            <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="#4F46E5" />
          </Field>
        </div>
        <Field label="Welcome message">
          <Input value={welcome} onChange={(e) => setWelcome(e.target.value)} placeholder="Hi! How can I help?" />
        </Field>

        {/* ── Generate / re-generate the API key + snippet */}
        {!apiKey && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            padding: "10px 12px", borderRadius: 8, background: `${COLORS.accent}10`,
            border: `1px solid ${COLORS.accent}35`,
          }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, lineHeight: 1.5, flex: 1 }}>
              Click <strong style={{ color: COLORS.accent }}>Generate snippet</strong> to mint a
              fresh widget-scoped API key for this embed. The key is shown
              once — copy the snippet right after.
            </div>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                padding: "7px 14px", borderRadius: 8, border: "none",
                background: COLORS.accent, color: "#fff",
                fontSize: 12, fontWeight: 600,
                cursor: generating ? "default" : "pointer", opacity: generating ? 0.6 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {generating ? "Generating…" : "Generate snippet"}
            </button>
          </div>
        )}

        {/* ── Snippet display */}
        {apiKey && (
          <div style={{
            padding: "8px 12px", borderRadius: 8,
            background: `${COLORS.green}10`, border: `1px solid ${COLORS.green}35`,
            fontSize: 11, color: COLORS.green, lineHeight: 1.5,
          }}>
            Key created. Copy the snippet now — the key value is not shown again.
            Revoke later via <strong>Admin → API Keys</strong>.
          </div>
        )}

        <div style={{ position: "relative" }}>
          <pre style={{
            margin: 0, padding: "12px 14px", borderRadius: 8,
            background: COLORS.bg, border: `1px solid ${COLORS.border}`,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11, lineHeight: 1.55, color: COLORS.text,
            whiteSpace: "pre-wrap", wordBreak: "break-all",
          }}>{snippet}</pre>
          <button
            onClick={handleCopy}
            disabled={!agent?.id}
            style={{
              position: "absolute", top: 8, right: 8,
              padding: "5px 12px", borderRadius: 6, border: "none",
              background: copied ? COLORS.green : COLORS.accent, color: "#fff",
              fontSize: 11, fontWeight: 600, cursor: "pointer",
              opacity: !agent?.id ? 0.5 : 1,
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>

        {apiKey && (
          <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
            Key id <strong style={{ color: COLORS.textMuted }}>{keyId}</strong>
            {" · "}prefix <strong style={{ color: COLORS.textMuted }}>{apiKey.slice(0, 14)}…</strong>
          </div>
        )}

        {error && (
          <div style={{
            padding: "8px 12px", borderRadius: 8,
            background: `${COLORS.red || "#ef4444"}15`,
            border: `1px solid ${COLORS.red || "#ef4444"}40`,
            color: COLORS.red || "#ef4444", fontSize: 12,
          }}>{error}</div>
        )}

        {/* Per-agent CORS allowlist — which sites this widget may load on.
            Fail-open: empty = works everywhere. */}
        {agent?.id && (
          <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14 }}>
            <WidgetAllowedDomains agentId={agent.id} />
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, color: COLORS.textMuted, letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 600 }}>
        {label}
      </span>
      {children}
    </label>
  );
}
